import {
  AsyncInteractor,
  IBlocAsyncInteractor
} from "@kernel/frontend-react-app";
import { MediaDeviceService } from "../services";
import { ActiveDevice, Stream } from "@domain";

export interface GetUserMediaService {
  getUserMedia: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
}

export interface StartMediaStreamInteractorProps {
  selectedDevice: ActiveDevice;
  videoElement: HTMLVideoElement;
  isScreenSource?: boolean; // Flag per indicare se usare screen capture
}

export class StartMediaStreamInteractor
  extends AsyncInteractor<
    GetUserMediaService,
    StartMediaStreamInteractorProps,
    Stream
  >
  implements IBlocAsyncInteractor<Stream, StartMediaStreamInteractorProps>
{
  public async execute({
    selectedDevice,
    videoElement,
    isScreenSource = false
  }: StartMediaStreamInteractorProps): Promise<Stream> {
    // Se è student mode (screen source), usa getDisplayMedia
    if (isScreenSource) {
      return this.initScreenSource(videoElement, selectedDevice);
    }

    // Altrimenti usa getUserMedia (camera)
    return this.initCameraSource(videoElement, selectedDevice);
  }

  /**
   * Inizializza lo stream dalla camera (default)
   */
  private async initCameraSource(
    videoElement: HTMLVideoElement,
    selectedDevice: ActiveDevice
  ): Promise<Stream> {
    const videoDevice = selectedDevice.video?.id.toString();
    const audioDevice = selectedDevice.audio?.id.toString();
    const constraints: MediaStreamConstraints = {};

    if (videoDevice) {
      constraints.video = { deviceId: { exact: videoDevice } };
    }

    if (audioDevice) {
      constraints.audio = { deviceId: { exact: audioDevice } };
    }

    try {
      const mediaStream = await this.service.getUserMedia(constraints);
      const stream = Stream.create({
        mediaStream,
        local: true,
        sourceType: "camera"
      });
      stream.start(videoElement);
      return stream;
    } catch (error) {
      console.log("Error getting user media", error);
      console.error(error);
      const emptyStream = Stream.create({
        mediaStream: new MediaStream(),
        local: true,
        sourceType: "camera"
      });
      emptyStream.start(videoElement);
      return emptyStream;
    }
  }

  /**
   * Inizializza lo stream dallo schermo (student mode)
   */
  private async initScreenSource(
    videoElement: HTMLVideoElement,
    selectedDevice: ActiveDevice
  ): Promise<Stream> {
    console.log("Initializing screen capture for student mode");

    const constraints: DisplayMediaStreamOptions = {
      video: {
        displaySurface: "monitor",
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      },
      audio: false
    };

    try {
      const mediaStream =
        await navigator.mediaDevices.getDisplayMedia(constraints);

      // Gestione evento di fine condivisione
      mediaStream.getVideoTracks()[0].onended = () => {
        console.log("User stopped screen sharing");
        this.cleanupStream(videoElement);
      };

      const stream = Stream.create({
        mediaStream,
        local: true,
        sourceType: "screen"
      });
      stream.start(videoElement);

      // Aggiorna il device come screen source
      selectedDevice.setVideoSource(null, true);

      return stream;
    } catch (error) {
      // Gestione NotAllowedError (utente nega permesso)
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        console.warn("Screen sharing permission denied by user");
      } else {
        console.error("Error initializing screen source:", error);
      }

      // Fallback: crea stream vuoto
      const emptyStream = Stream.create({
        mediaStream: new MediaStream(),
        local: true,
        sourceType: "camera"
      });
      emptyStream.start(videoElement);
      return emptyStream;
    }
  }

  /**
   * Pulisce lo stream dall'elemento video
   */
  private cleanupStream(videoElement: HTMLVideoElement): void {
    if (videoElement.srcObject) {
      const tracks = (videoElement.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      videoElement.srcObject = null;
    }
  }

  public static create() {
    const service = new MediaDeviceService();
    return new StartMediaStreamInteractor(service);
  }
}
